import { useCallback, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { RiArrowRightSLine } from '@remixicon/react';
import { NAV_SECTIONS } from '@/app/navigation/nav.config';
import type { NavItem, NavSection } from '@/app/navigation/types';
import { useThemeStore } from '@/stores/themeStore';
import { useAuthStore } from '@/stores/authStore';
import { BrandMark } from './BrandMark';

interface SidebarProps {
  /**
   * Menü ağacı. Verilmezse yönetim arayüzünün menüsü kullanılır.
   *
   * Kiracı portalı AYNI kabuğu kullanır, yalnızca menü içeriği farklıdır;
   * ikinci bir menü bileşeni yazmak, birinde yapılan düzeltmenin diğerine
   * geçmemesi demekti.
   */
  sections?: NavSection[];
}

/**
 * Sol menü.
 *
 * Menü ağacı `nav.config.ts`'ten gelir — bu bileşen hiçbir yol veya etiket
 * bilmez. Yeni modül eklemek için buraya dokunulmaz.
 */
export function Sidebar({ sections = NAV_SECTIONS }: SidebarProps = {}) {
  const { pathname } = useLocation();
  const mobileNavOpen = useThemeStore((s) => s.mobileNavOpen);
  const setMobileNavOpen = useThemeStore((s) => s.setMobileNavOpen);

  /**
   * Aktif sayfayı içeren grup — varsayılan olarak AÇIK olmalı. Kullanıcı
   * sayfayı yenilediğinde veya doğrudan bir alt sayfaya girdiğinde menüde
   * nerede olduğunu görebilmeli.
   */
  const activeGroupId = useMemo(() => findActiveGroupId(sections, pathname), [sections, pathname]);

  /*
    AÇIK/KAPALI DURUMU EFEKTLE DEĞİL, TÜRETMEYLE ÇÖZÜLÜR.

    "Aktif grubu açık tut" kuralını bir `useEffect` içinde setState ile
    uygulamak, her yol değişiminde fazladan bir render turu üretir ve o
    turda menü grubu KAPALI görünür — kullanıcı, sayfa her değiştiğinde
    menünün bir an kapanıp açıldığını görür.

    Bunun yerine yalnızca kullanıcının BİLİNÇLİ tercihi saklanır.
    Grubun açık olup olmadığı her render'da hesaplanır:
    kullanıcı tercihi varsa o, yoksa "aktif grup mu?" kuralı.

    Yan fayda: kullanıcı aktif grubu elle kapatabilir ve kapalı kalır —
    efekt tabanlı çözümde bu mümkün değildi, efekt her yol değişiminde
    grubu tekrar açıyordu.
  */
  const [userToggled, setUserToggled] = useState<Record<string, boolean>>({});

  const isGroupOpen = useCallback(
    (id: string) => userToggled[id] ?? id === activeGroupId,
    [userToggled, activeGroupId],
  );

  const toggleGroup = useCallback(
    (id: string) => {
      setUserToggled((prev) => ({ ...prev, [id]: !(prev[id] ?? id === activeGroupId) }));
    },
    [activeGroupId],
  );

  // Mobilde bir bağlantıya tıklanınca menü kapanmalı.
  const handleNavigate = useCallback(() => {
    if (window.innerWidth < 992) setMobileNavOpen(false);
  }, [setMobileNavOpen]);

  /*
    YETKİYE GÖRE FİLTRELEME.

    Kullanıcının açamayacağı bir bağlantıyı göstermek iki şekilde zarar
    verir: tıklandığında 403 ekranı çıkar (sistem bozukmuş gibi görünür)
    ve menü, kullanıcının işine yaramayan öğelerle uzayıp gerçekten
    kullandıklarını görünmez kılar.

    Tüm öğeleri elenen bölüm başlığı da gizlenir; boş bir "SİSTEM"
    başlığı, altında hiçbir şey olmadan durmamalıdır.

    NOT: Bu bir GÜVENLİK önlemi değildir. Gerçek koruma sunucudadır ve
    menü kodu tamamen silinse bile API 403 döner.
  */
  const canAny = useAuthStore((s) => s.canAny);

  const visibleSections = useMemo(
    () =>
      sections.map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          if (!item.permission) return true;
          const needed = Array.isArray(item.permission) ? item.permission : [item.permission];
          return canAny(needed);
        }),
      })).filter((section) => section.items.length > 0),
    [canAny, sections],
  );

  return (
    <>
      {mobileNavOpen && (
        <div
          className="sidebar-backdrop d-lg-none"
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside className={`sidebar${mobileNavOpen ? ' is-open' : ''}`}>
        <div className="sidebar__brand">
          <BrandMark className="sidebar__brand-mark" />
          <span className="sidebar__brand-text">MallOS</span>
        </div>

        <nav className="sidebar__nav" aria-label="Ana menü">
          {visibleSections.map((section) => (
            <div key={section.id}>
              <div className="sidebar__section-label">{section.label}</div>
              <ul className="list-unstyled mb-0">
                {section.items.map((item) => (
                  <SidebarNode
                    key={item.id}
                    item={item}
                    isOpen={isGroupOpen(item.id)}
                    hasActiveChild={containsPath(item, pathname)}
                    onToggle={toggleGroup}
                    onNavigate={handleNavigate}
                  />
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}

interface SidebarNodeProps {
  item: NavItem;
  isOpen: boolean;
  /** Alt sayfalarından biri açık mı? İkon modunda tek aktiflik işareti budur. */
  hasActiveChild?: boolean;
  onToggle: (id: string) => void;
  onNavigate: () => void;
}

function SidebarNode({ item, isOpen, hasActiveChild, onToggle, onNavigate }: SidebarNodeProps) {
  const Icon = item.icon;
  const badge = item.badge?.();

  // Alt menülü öğe: buton olarak render edilir (yol taşımaz).
  if (item.children?.length) {
    return (
      <li className="sidebar__item">
        <button
          type="button"
          className={`sidebar__link${hasActiveChild ? ' has-active-child' : ''}`}
          aria-expanded={isOpen}
          onClick={() => onToggle(item.id)}
          /*
            İKON MODUNDA ETİKET GÖRÜNMEZ — BAŞLIK (title) TEK İPUCUDUR.

            Menüyü tamamen açmadan hangi ikonun ne olduğunu anlamanın
            başka yolu yok; hedefi hatırlamak için her seferinde menüyü
            açmak zorunda kalmak, ikon modunu kullanılmaz kılar.
          */
          title={item.label}
        >
          {Icon && (
            <span className="sidebar__icon">
              <Icon size={18} aria-hidden="true" />
            </span>
          )}
          <span className="sidebar__label">{item.label}</span>
          <RiArrowRightSLine className="sidebar__chevron" size={16} aria-hidden="true" />
        </button>

        {isOpen && (
          <ul className="sidebar__submenu">
            {item.children.map((child) => (
              <SidebarNode
                key={child.id}
                item={child}
                isOpen={false}
                onToggle={onToggle}
                onNavigate={onNavigate}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }

  if (!item.to) return null;

  return (
    <li className="sidebar__item">
      <NavLink
        to={item.to}
        onClick={onNavigate}
        className={({ isActive }) => `sidebar__link${isActive ? ' is-active' : ''}`}
        // aria-current NavLink tarafından otomatik eklenir; ekran okuyucu
        // kullanıcısı hangi sayfada olduğunu duyar.
        title={item.comingSoon ? `${item.label} — yakında` : item.label}
      >
        {Icon && (
          <span className="sidebar__icon">
            <Icon size={18} aria-hidden="true" />
          </span>
        )}
        <span className="sidebar__label">{item.label}</span>

        {badge && (
          <span className={`sidebar__badge badge-${badge.tone}-transparent`}>{badge.text}</span>
        )}

        {item.comingSoon && !badge && (
          <span className="sidebar__badge badge-secondary-transparent">yakında</span>
        )}
      </NavLink>
    </li>
  );
}

/** Öğe (veya alt öğelerinden biri) verilen yolda mı? */
function containsPath(item: NavItem, pathname: string): boolean {
  const matches = (to?: string): boolean =>
    Boolean(to) && (pathname === to || pathname.startsWith(`${to}/`));

  if (matches(item.to)) return true;
  return (item.children ?? []).some((c) => matches(c.to));
}

/** Verilen yolu içeren üst menü öğesinin id'sini bulur. */
function findActiveGroupId(sections: NavSection[], pathname: string): string | null {
  for (const section of sections) {
    for (const item of section.items) {
      if (!item.children?.length) continue;

      const hit = item.children.some(
        (c) => c.to && (pathname === c.to || pathname.startsWith(`${c.to}/`)),
      );
      if (hit) return item.id;
    }
  }
  return null;
}
